import { View } from "ol";
import { useEffect, useRef } from "react";
import { Map as MapOlType } from "ol";
import { fromLonLat } from "ol/proj";

import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";

import type { Extent } from "ol/extent";
import { DEFAULT_MAP_CENTER } from "../map-constant";
import GeoJSON from "ol/format/GeoJSON";
import { createStyleFn } from "../helpers";
import Control from "ol/control/Control";

export const useOpenLayersMap = ({
	extent,
	zoom,
	handleChangeZoom,
	allowInteraction,
	controls,
}: {
	controls: Control[];
	extent?: Extent;
	zoom: number;
	handleChangeZoom: (n: number) => void;
	allowInteraction: boolean;
}) => {
	const markerLayerRef = useRef<VectorLayer | null>(null);
	const vectorSourceRef = useRef<VectorSource | null>(null);
	const observationLayerRef = useRef<VectorLayer | null>(null);
	const observationSourceRef = useRef<VectorSource | null>(null);
	const mapRefOl = useRef<MapOlType | null>(null);
	const mapRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		vectorSourceRef.current = new VectorSource();
		markerLayerRef.current = new VectorLayer({
			source: vectorSourceRef.current,
			zIndex: 30,
		});

		observationSourceRef.current = new VectorSource();
		observationLayerRef.current = new VectorLayer({
			source: observationSourceRef.current,
			zIndex: 40,
		});

		const view = new View({
			extent,
			center: fromLonLat(DEFAULT_MAP_CENTER),
			zoom: zoom,
			minZoom: 5,
			maxZoom: 11,
		});

		view.on("change:resolution", () => {
			handleChangeZoom(Number(view.getZoom()?.toFixed(1)));
		});

		mapRefOl.current = new MapOlType({
			target: mapRef.current ?? undefined,
			view,
			layers: [markerLayerRef.current, observationLayerRef.current],

			interactions: allowInteraction ? undefined : [],
			controls,
		});

		return () => {
			mapRefOl.current?.setTarget(undefined);
			mapRefOl.current = null;
		};
	}, []);

	useEffect(() => {
		if (!mapRefOl.current) return;

		const layer = new VectorLayer({
			source: new VectorSource({
				url: "https://kuwait.exelys.io/tmp_raster/map.json",
				format: new GeoJSON(),
			}),
			style: createStyleFn(),
			declutter: true,
			zIndex: 10,
		});

		mapRefOl.current.getLayers().insertAt(0, layer);

		return () => {
			mapRefOl.current?.removeLayer(layer);
		};
	}, []);

	return {
		mapRef,
		mapRefOl,
		vectorSourceRef,
		observationSourceRef,
	};
};
